import axios from 'axios';

/** Same origin on production when VITE_* is unset (avoids silent localhost calls). */
const baseURL =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');

export const patientPortalApi = axios.create({ baseURL });

/** Login / OTP verify response from patient-app auth APIs. */
export type PatientAuthResult = {
  token?: string;
  needsSetPassword?: boolean;
  setupToken?: string;
  patient?: {
    id: string;
    name: string;
    uhid: string | null;
    email?: string | null;
  };
  promptAddEmail?: boolean;
};

patientPortalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('patient_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

patientPortalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('patient_token');
      const path = window.location.pathname;
      const isPublicPatientPage =
        path.startsWith('/patient-loyalty/login') ||
        path.startsWith('/patient-loyalty/forgot-password') ||
        path.startsWith('/patient-loyalty/reset-password') ||
        path.startsWith('/patient-loyalty/delete-account');
      if (!isPublicPatientPage) {
        window.location.href = '/patient-loyalty/login';
      }
    }
    return Promise.reject(error);
  }
);

function authHeaders() {
  const token = localStorage.getItem('patient_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function downloadPatientPdf(path: string, filename: string) {
  const res = await patientPortalApi.get(path, {
    responseType: 'blob',
    headers: authHeaders(),
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const patientPortal = {
  getBranding: () => patientPortalApi.get('/api/patient-app/v1/branding').then((r) => r.data.data),
  lookupAccounts: (identifier: string, hospitalId?: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/lookup', { identifier, hospitalId })
      .then(
        (r) =>
          r.data.data as {
            loginType: string;
            patients: Array<{
              id: string;
              name: string;
              uhid: string | null;
              email?: string | null;
              hasPassword?: boolean;
            }>;
          }
      ),
  loginWithPassword: (patientId: string, password: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/login/password', { patientId, password })
      .then((r) => r.data.data as PatientAuthResult),
  sendOtp: (
    payload:
      | string
      | {
          patientId?: string;
          delivery?: string;
          identifier?: string;
          phone?: string;
          hospitalId?: string;
        }
  ) => {
    const body =
      typeof payload === 'string'
        ? { phone: payload }
        : payload;
    return patientPortalApi.post('/api/patient-app/v1/auth/otp/send', body).then((r) => r.data.data);
  },
  verifyOtp: (
    payload:
      | string
      | {
          patientId: string;
          otp: string;
          identifier?: string;
          phone?: string;
          hospitalId?: string;
        },
    otpArg?: string
  ) => {
    const body =
      typeof payload === 'string'
        ? { phone: payload, otp: otpArg }
        : payload;
    return patientPortalApi
      .post('/api/patient-app/v1/auth/otp/verify', body)
      .then((r) => r.data.data as PatientAuthResult);
  },
  forgotPassword: (payload: { email: string; patientId?: string }) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/forgot-password', payload)
      .then(
        (r) =>
          r.data.data as {
            message: string;
            requiresPatientSelection?: boolean;
            patients?: Array<{ id: string; name: string }>;
          }
      ),
  resetPassword: (token: string, newPassword: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/reset-password', { token, newPassword })
      .then((r) => r.data.data),
  setPasswordInitial: (
    payload:
      | { setupToken: string; newPassword: string }
      | {
          patientId: string;
          identifier: string;
          newPassword: string;
          hospitalId?: string;
        }
  ) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/set-password/initial', payload)
      .then((r) => r.data.data as PatientAuthResult),
  forgotPasswordOtpSend: (payload: { identifier: string; patientId?: string }) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/forgot-password/otp/send', payload)
      .then(
        (r) =>
          r.data.data as {
            requiresPatientSelection?: boolean;
            patients?: Array<{ id: string; name: string; uhid: string | null }>;
            patientId?: string;
            devOtp?: string;
            destinationMasked?: string;
          }
      ),
  forgotPasswordOtpVerify: (patientId: string, otp: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/forgot-password/otp/verify', { patientId, otp })
      .then((r) => r.data.data as { resetToken: string; patient: { id: string; name: string } }),
  setPassword: (newPassword: string, currentPassword?: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/auth/set-password', { newPassword, currentPassword })
      .then((r) => r.data.data),
  getMe: () => patientPortalApi.get('/api/patient-app/v1/me').then((r) => r.data.data),
  updateEmail: (email: string) =>
    patientPortalApi
      .patch('/api/patient-app/v1/me/email', { email })
      .then((r) => r.data.data as { success: boolean; email: string }),
  getLoyalty: () => patientPortalApi.get('/api/patient-app/v1/loyalty').then((r) => r.data.data),
  getWallet: () => patientPortalApi.get('/api/patient-app/v1/wallet').then((r) => r.data.data),
  getOffers: () => patientPortalApi.get('/api/patient-app/v1/offers').then((r) => r.data.data),
  getAnnouncements: () =>
    patientPortalApi.get('/api/patient-app/v1/announcements').then((r) => {
      const data = r.data?.data;
      return Array.isArray(data) ? data : [];
    }),
  redeemPoints: (points: number, purpose: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/loyalty/redeem', { points, purpose })
      .then((r) => r.data.data),
  getAppointments: () => patientPortalApi.get('/api/patient-app/v1/appointments').then((r) => r.data.data),
  getBookingDoctors: () =>
    patientPortalApi.get('/api/patient-app/v1/booking/doctors').then((r) => r.data.data),
  getBookingSlots: (doctorId: string, date: string) =>
    patientPortalApi
      .get('/api/patient-app/v1/booking/slots', { params: { doctorId, date } })
      .then((r) => r.data.data),
  bookAppointment: (doctorId: string, scheduledAt: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/booking', { doctorId, scheduledAt })
      .then((r) => r.data.data),
  getBills: () => patientPortalApi.get('/api/patient-app/v1/bills').then((r) => r.data.data),
  getPrescriptions: () =>
    patientPortalApi.get('/api/patient-app/v1/prescriptions').then((r) => r.data.data),
  getLabReports: () =>
    patientPortalApi.get('/api/patient-app/v1/lab-reports').then((r) => r.data.data),
  getDischargeSummaries: () =>
    patientPortalApi.get('/api/patient-app/v1/discharge-summaries').then((r) => r.data.data),
  downloadBillPdf: (id: string, billNumber: string) =>
    downloadPatientPdf(`/api/patient-app/v1/bills/${id}/pdf`, `bill-${billNumber}.pdf`),
  downloadPrescriptionPdf: (id: string, num: string) =>
    downloadPatientPdf(`/api/patient-app/v1/prescriptions/${id}/pdf`, `rx-${num}.pdf`),
  downloadLabPdf: (id: string) =>
    downloadPatientPdf(`/api/patient-app/v1/lab-reports/${id}/pdf`, `lab-${id}.pdf`),
  downloadDischargePdf: (admissionId: string, ipd?: string) =>
    downloadPatientPdf(
      `/api/patient-app/v1/discharge-summaries/${admissionId}/pdf`,
      `discharge-${ipd ?? admissionId}.pdf`
    ),
  getFamily: () => patientPortalApi.get('/api/patient-app/v1/family').then((r) => r.data.data),
  registerFamilyMember: (data: {
    name: string;
    phone: string;
    dob: string;
    gender: string;
    relationship: string;
    email?: string;
    address?: string;
    preExistingCondition?: string;
  }) =>
    patientPortalApi.post('/api/patient-app/v1/family/register', data).then((r) => r.data.data),
  linkFamily: (relativePhone: string, relationship: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/family/link', { relativePhone, relationship })
      .then((r) => r.data.data),
  getReferrals: () => patientPortalApi.get('/api/patient-app/v1/referrals').then((r) => r.data.data),
  submitReferral: (data: {
    patientName: string;
    phone: string;
    remarks: string;
    gender?: string;
    dob?: string;
    email?: string;
    location?: string;
    alternatePhone?: string;
  }) => patientPortalApi.post('/api/patient-app/v1/referrals', data).then((r) => r.data.data),
  getNotifications: () =>
    patientPortalApi.get('/api/patient-app/v1/notifications').then((r) => r.data.data),
  getChallenges: () => patientPortalApi.get('/api/patient-app/v1/challenges').then((r) => r.data.data),
  markNotificationRead: (id: string) =>
    patientPortalApi.post(`/api/patient-app/v1/notifications/${id}/read`).then((r) => r.data.data),
  setPoolHead: (headPatientId: string | null) =>
    patientPortalApi.post('/api/patient-app/v1/family/pool-head', { headPatientId }).then((r) => r.data.data),
  getContactHospitals: () =>
    patientPortalApi.get('/api/patient-app/v1/contact/hospitals').then((r) => r.data.data),
  submitContactQuery: (message: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/contact/query', { message })
      .then((r) => r.data.data),
  getAccountDeletionInfo: () =>
    patientPortalApi.get('/api/patient-app/v1/account/deletion-info').then((r) => r.data.data),
  requestAccountDeletionPublic: (data: { phone: string; uhid?: string; reason: string }) =>
    patientPortalApi
      .post('/api/patient-app/v1/account/deletion-request/public', data)
      .then((r) => r.data.data),
  requestAccountDeletion: (reason?: string) =>
    patientPortalApi
      .post('/api/patient-app/v1/account/deletion-request', { reason })
      .then((r) => r.data.data),
};
